import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, TreeDeciduous, Mountain, Hammer, Leaf, Star } from 'lucide-react';
import Logo from '../components/Logo';
import { companyInfo, heroImages, services, galleryItems, testimonials } from '../data/mockData';
import '../styles/rooted.css';

const RootedHome = () => {
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  // Rotate hero background images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Reset messages
    setFormError(null);
    setFormSuccess(false);
    
    // Validate form
    if (!formData.name || !formData.email || !formData.message) {
      setFormError('Please fill in all required fields');
      return;
    }
    
    setFormSubmitted(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit form');
      }
      
      const result = await response.json();
      console.log('Form submitted successfully:', result);
      
      // Show success message and reset form
      setFormSuccess(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setFormSubmitted(false);
      
      // Auto-hide success message after 8 seconds
      setTimeout(() => {
        setFormSuccess(false);
      }, 8000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormError('There was an error submitting the form. Please try again or call us at 215-534-7848.');
      setFormSubmitted(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const iconMap = {
    TreeDeciduous,
    Mountain,
    Hammer,
    Leaf
  };

  return (
    <div className="rooted-home">
      {/* Navigation */}
      <nav className="navigation">
        <div className="nav-container">
          <a href="#hero" className="nav-logo">
            <Logo className="nav-logo-img" size={40} />
          </a>
          <ul className="nav-menu">
            <li><a href="#services" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}>SERVICES</a></li>
            <li><a href="#gallery" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('gallery'); }}>GALLERY</a></li>
            <li><a href="#reviews" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('reviews'); }}>REVIEWS</a></li>
            <li><a href="/watering-schedule" className="nav-link" style={{background: 'rgba(255,255,255,0.2)', padding: '0.5rem 1rem', borderRadius: '4px'}}>WATERING</a></li>
            <li><a href="#about" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}>ABOUT</a></li>
            <li><a href="#contact" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>CONTACT</a></li>
          </ul>
        </div>
      </nav>

      {/* Hero Section - Full Screen Showcase Photo */}
      <section 
        id="hero"
        style={{
          position: 'relative',
          height: '100vh',
          minHeight: '700px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Background Image - Your Best Work Front and Center */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `url('https://customer-assets.emergentagent.com/job_boulder-tree-work/artifacts/hsmdmqy6_IMG_0345.jpeg') center/cover no-repeat`,
          filter: 'brightness(0.7)'
        }} />
        
        {/* Content Overlay */}
        <div style={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'center',
          color: 'white',
          padding: '2rem',
          maxWidth: '900px'
        }}>
          <Logo size={120} style={{ marginBottom: '2rem', filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.5))' }} />
          
          <h1 style={{
            fontSize: 'clamp(2.5rem, 8vw, 5rem)',
            fontWeight: 'bold',
            marginBottom: '1.5rem',
            textShadow: '0 4px 20px rgba(0,0,0,0.8)',
            lineHeight: '1.1'
          }}>
            {companyInfo.name}
          </h1>
          
          <p style={{
            fontSize: 'clamp(1.125rem, 3vw, 1.5rem)',
            marginBottom: '2.5rem',
            textShadow: '0 2px 10px rgba(0,0,0,0.8)',
            lineHeight: '1.6'
          }}>
            {companyInfo.tagline}
          </p>
          
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href={`tel:${companyInfo.phone}`}
              style={{
                padding: '1.25rem 2.5rem',
                backgroundColor: '#7f1d1d',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontSize: '1.125rem',
                fontWeight: '600',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#991b1b';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#7f1d1d';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              📞 Call: {companyInfo.phone}
            </a>
            
            <button
              onClick={() => scrollToSection('contact')}
              style={{
                padding: '1.25rem 2.5rem',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                border: '2px solid white',
                borderRadius: '8px',
                fontSize: '1.125rem',
                fontWeight: '600',
                cursor: 'pointer',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
            >
              Get Free Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-spacing-large" style={{ background: 'var(--color-off-white)' }}>
        <div className="container-rooted">
          <h2 className="section-title" style={{ textAlign: 'center' }}>Our Specialties</h2>
          <p className="body-text" style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 3rem' }}>
            We bring decades of expertise in Japanese-inspired landscape construction, 
            focusing on authentic techniques and natural materials.
          </p>
          <div className="services-grid">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <div key={service.id} className="service-card">
                  <div className="service-image-wrapper">
                    <img src={service.image} alt={service.title} className="service-image" />
                  </div>
                  <div className="service-content">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      {IconComponent && <IconComponent size={24} color="var(--color-primary)" />}
                      <h3 className="service-title">{service.title}</h3>
                    </div>
                    <p className="service-description">{service.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="section-spacing-large">
        <div className="container-rooted">
          <h2 className="section-title" style={{ textAlign: 'center' }}>Our Work</h2>
          <p className="body-text" style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 3rem' }}>
            Browse our portfolio of completed projects showcasing flagstone mastery, 
            boulder placement, and tree installation artistry.
          </p>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
            gap: '2rem' 
          }}>
            {galleryItems.map((item) => (
              <div 
                key={item.id}
                style={{
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  overflow: 'hidden',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  background: 'white'
                }}
                onClick={() => setLightboxImage(item)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }}
              >
                {/* Image - Full size, no overlay */}
                <div style={{
                  width: '100%',
                  height: '400px',
                  overflow: 'hidden',
                  background: '#f5f5f5'
                }}>
                  <img 
                    src={item.image} 
                    alt={item.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                </div>
                
                {/* Labels below image */}
                <div style={{
                  padding: '1.25rem'
                }}>
                  <div style={{
                    display: 'inline-block',
                    padding: '0.25rem 0.75rem',
                    background: 'var(--color-primary)',
                    color: 'white',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    marginBottom: '0.75rem'
                  }}>
                    {item.category}
                  </div>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '700',
                    color: 'var(--color-text)',
                    marginBottom: '0.5rem',
                    lineHeight: '1.4'
                  }}>
                    {item.title}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.95)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem',
                cursor: 'pointer'
              }}
              onClick={() => setLightboxImage(null)}
            >
              <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
                <img 
                  src={lightboxImage.image} 
                  alt={lightboxImage.title}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '90vh',
                    width: 'auto',
                    height: 'auto',
                    borderRadius: '8px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  bottom: '-60px',
                  left: 0,
                  right: 0,
                  textAlign: 'center',
                  color: 'white'
                }}>
                  <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', fontFamily: 'var(--font-serif)' }}>
                    {lightboxImage.title}
                  </h3>
                  <p style={{ fontSize: '1rem', opacity: 0.8 }}>{lightboxImage.category}</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxImage(null);
                  }}
                  style={{
                    position: 'absolute',
                    top: '-40px',
                    right: '0',
                    background: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    fontSize: '24px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    color: '#000'
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          )}

      {/* About Section */}
      <section id="about" className="section-spacing-large" style={{ background: 'var(--color-off-white)' }}>
        <div className="container-rooted">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '3rem', alignItems: 'center' }}>
            <div>
              <h2 className="section-title">Craftsmanship Rooted in Tradition</h2>
              <div className="body-text" style={{ marginBottom: '1.5rem' }}>
                <p style={{ marginBottom: '1rem' }}>
                  At Rooted Landscape and Hardscape Specialist, we don't just install landscapes—we 
                  craft living works of art inspired by centuries-old Japanese garden techniques.
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  Our specialty is <strong>flagstone work</strong>, where each stone is carefully selected 
                  and placed to create natural, flowing pathways and patios that age beautifully. We also 
                  excel in <strong>strategic boulder placement</strong> and <strong>large tree moving</strong>, 
                  bringing mature specimens to your property with expert care.
                </p>
                <p>
                  Every project is approached with the precision and artistry that Japanese-inspired 
                  landscaping demands. We hand-dig, hand-place, and hand-craft each element to create 
                  spaces that inspire tranquility and connection with nature.
                </p>
              </div>
              <button 
                className="btn-primary"
                onClick={() => scrollToSection('contact')}
              >
                Start Your Project
              </button>
            </div>
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_boulder-tree-work/artifacts/304vrz1k_IMG_0299.jpeg" 
                alt="Flagstone craftsmanship detail" 
                style={{ width: '100%', height: 'auto', borderRadius: '4px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="reviews" className="section-spacing-large" style={{ background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-tertiary) 100%)' }}>
        <div className="container-rooted">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.2)', padding: '0.5rem 1.5rem', borderRadius: '50px', marginBottom: '1rem' }}>
              <p style={{ color: 'white', fontSize: '0.875rem', fontWeight: '600', letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0 }}>
                ⭐ Customer Reviews
              </p>
            </div>
            <h2 className="section-title section-title-light" style={{ fontSize: '3rem', marginBottom: '1rem' }}>
              What Our Clients Say
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.125rem', maxWidth: '600px', margin: '0 auto 2rem' }}>
              Real feedback from homeowners who trusted us with their landscape projects
            </p>
            <a
              href="/leave-review"
              style={{
                display: 'inline-block',
                padding: '1rem 2rem',
                background: 'white',
                color: 'var(--color-primary)',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '1rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
              }}
            >
              ⭐ Leave a Review
            </a>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            {testimonials.map((testimonial) => (
              <div 
                key={testimonial.id} 
                style={{ 
                  background: 'white',
                  padding: '2.5rem', 
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  cursor: 'default',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 15px 50px rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 40px rgba(0,0,0,0.15)';
                }}
              >
                {/* Quote icon */}
                <div style={{ 
                  fontSize: '4rem', 
                  lineHeight: '1', 
                  color: 'var(--color-primary)', 
                  opacity: '0.15',
                  position: 'absolute',
                  top: '1rem',
                  left: '1.5rem',
                  fontFamily: 'Georgia, serif'
                }}>
                  "
                </div>
                
                <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', position: 'relative', zIndex: 1 }}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} fill="#FFA500" color="#FFA500" />
                  ))}
                </div>
                
                <p style={{ 
                  marginBottom: '1.5rem', 
                  fontStyle: 'italic',
                  fontSize: '1.0625rem',
                  lineHeight: '1.7',
                  color: 'var(--color-text)',
                  position: 'relative',
                  zIndex: 1
                }}>
                  "{testimonial.text}"
                </p>
                
                <div style={{ borderTop: '2px solid var(--color-gray-200)', paddingTop: '1rem', position: 'relative', zIndex: 1 }}>
                  <p style={{ color: 'var(--color-primary)', marginBottom: '0.25rem', fontWeight: '600', fontSize: '1.0625rem' }}>
                    {testimonial.name}
                  </p>
                  <p style={{ color: 'var(--color-gray-600)', fontSize: '0.875rem' }}>
                    📍 {testimonial.location}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section section-spacing-large">
        <div className="container-rooted">
          <h2 className="section-title section-title-light" style={{ textAlign: 'center' }}>Get In Touch</h2>
          <p className="body-text body-text-light" style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 3rem' }}>
            Ready to transform your landscape? Contact us for a consultation and let's discuss your vision.
          </p>
          <div className="contact-grid">
            <div className="contact-info">
              <div className="contact-item">
                <Phone className="contact-icon" size={24} />
                <div>
                  <p className="body-text body-text-light" style={{ margin: 0, fontWeight: 600 }}>Phone</p>
                  <a href={`tel:${companyInfo.phone}`} style={{ color: 'var(--color-white)', textDecoration: 'none' }}>
                    {companyInfo.phone}
                  </a>
                </div>
              </div>
              <div className="contact-item">
                <Mail className="contact-icon" size={24} />
                <div>
                  <p className="body-text body-text-light" style={{ margin: 0, fontWeight: 600 }}>Email</p>
                  <a href={`mailto:${companyInfo.email}`} style={{ color: 'var(--color-white)', textDecoration: 'none' }}>
                    {companyInfo.email}
                  </a>
                </div>
              </div>
              <div className="contact-item">
                <MapPin className="contact-icon" size={24} />
                <div>
                  <p className="body-text body-text-light" style={{ margin: 0, fontWeight: 600 }}>Service Area</p>
                  <p className="body-text body-text-light" style={{ margin: 0 }}>
                    Philadelphia & Surrounding Counties
                  </p>
                </div>
              </div>
            </div>
            
            <form className="contact-form" onSubmit={handleFormSubmit}>
              {/* Success Message */}
              {formSuccess && (
                <div style={{
                  padding: '1rem 1.5rem',
                  marginBottom: '1.5rem',
                  backgroundColor: '#10b981',
                  color: 'white',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  textAlign: 'center'
                }}>
                  ✓ Thank you! We received your message and will get back to you shortly.
                </div>
              )}
              
              {/* Error Message */}
              {formError && (
                <div style={{
                  padding: '1rem 1.5rem',
                  marginBottom: '1.5rem',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  textAlign: 'center'
                }}>
                  ⚠ {formError}
                </div>
              )}
              
              <div className="form-group">
                <label className="form-label" htmlFor="name">Name *</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  className="form-input" 
                  required 
                  value={formData.name}
                  onChange={handleFormChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="email">Email *</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  className="form-input" 
                  required 
                  value={formData.email}
                  onChange={handleFormChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="phone">Phone</label>
                <input 
                  type="tel" 
                  id="phone" 
                  name="phone" 
                  className="form-input"
                  value={formData.phone}
                  onChange={handleFormChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="message">Message *</label>
                <textarea 
                  id="message" 
                  name="message" 
                  className="form-textarea" 
                  required
                  value={formData.message}
                  onChange={handleFormChange}
                ></textarea>
              </div>
              <button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={formSubmitted}>
                {formSubmitted ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container-rooted">
          <div className="footer-content">
            <div>
              <Logo size={60} />
              <h3 className="footer-brand" style={{ marginTop: '1rem' }}>{companyInfo.name}</h3>
              <p className="caption-text" style={{ color: 'var(--color-gray-400)', marginTop: '0.5rem' }}>
                {companyInfo.tagline}
              </p>
            </div>
            <div>
              <h4 style={{ color: 'var(--color-white)', marginBottom: '1rem', fontFamily: 'var(--font-sans)' }}>Quick Links</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <a href="#services" className="nav-link" style={{ textTransform: 'none', fontSize: '0.95rem' }} onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}>Services</a>
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <a href="#gallery" className="nav-link" style={{ textTransform: 'none', fontSize: '0.95rem' }} onClick={(e) => { e.preventDefault(); scrollToSection('gallery'); }}>Gallery</a>
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <a href="#about" className="nav-link" style={{ textTransform: 'none', fontSize: '0.95rem' }} onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}>About</a>
                </li>
                <li>
                  <a href="#contact" className="nav-link" style={{ textTransform: 'none', fontSize: '0.95rem' }} onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>Contact</a>
                </li>
              </ul>
            </div>
            <div>
              <h4 style={{ color: 'var(--color-white)', marginBottom: '1rem', fontFamily: 'var(--font-sans)' }}>Contact Info</h4>
              <p className="caption-text" style={{ color: 'var(--color-gray-400)', marginBottom: '0.5rem' }}>
                <a href={`tel:${companyInfo.phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>{companyInfo.phone}</a>
              </p>
              <p className="caption-text" style={{ color: 'var(--color-gray-400)' }}>
                <a href={`mailto:${companyInfo.email}`} style={{ color: 'inherit', textDecoration: 'none' }}>{companyInfo.email}</a>
              </p>
            </div>
          </div>
          <div className="footer-copyright">
            <p>&copy; {new Date().getFullYear()} {companyInfo.name}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RootedHome;